import { useLayoutEffect, useRef, useState } from 'react'

import { useAnimationFrame } from '../../../../../hooks'
import {
  getRequestProfileHeaderFooterActionsVisibleCount,
  getRequestProfileHeaderFooterActionWidths,
  getRequestProfileHeaderFooterCachedActionWidths,
} from '../../RequestProfileHeaderFooter.utils'

export function useRequestProfileHeaderFooterMeasure({ actions }) {
  const [visibleActionsCount, setVisibleActionsCount] = useState(actions.length)

  const addNewTextRef = useRef(null)
  const addNewTextMeasureRef = useRef(null)
  const containerRef = useRef(null)
  const containerResizeObserverRef = useRef(null)
  const moreButtonMeasureRef = useRef(null)
  const actionButtonRefs = useRef({})
  const actionWidthsByKeyRef = useRef({})
  const actionsRef = useRef(actions)

  function handleMeasure() {
    if (containerRef.current) {
      const currentActions = actionsRef.current
      const { actionWidths, actionWidthsByKey } =
        getRequestProfileHeaderFooterActionWidths({
          actionButtonRefs: actionButtonRefs.current,
          actions: currentActions,
          actionWidthsByKey: actionWidthsByKeyRef.current,
        })

      actionWidthsByKeyRef.current = actionWidthsByKey

      const addNewTextWidth =
        addNewTextMeasureRef.current?.offsetWidth ||
        addNewTextRef.current?.offsetWidth ||
        0

      const containerStyles = window.getComputedStyle(containerRef.current)
      const containerWidth =
        containerRef.current.clientWidth -
        parseFloat(containerStyles.paddingLeft) -
        parseFloat(containerStyles.paddingRight)

      const nextVisibleActionsCount =
        getRequestProfileHeaderFooterActionsVisibleCount({
          actionWidths,
          addNewTextWidth,
          containerWidth,
          moreButtonWidth: moreButtonMeasureRef.current?.offsetWidth || 0,
        })

      setVisibleActionsCount(previousValue => {
        return previousValue === nextVisibleActionsCount
          ? previousValue
          : nextVisibleActionsCount
      })
    }
  }

  const [scheduleMeasure, cancelMeasure] = useAnimationFrame(handleMeasure)

  function handleContainerRef(node) {
    containerRef.current = node

    containerResizeObserverRef.current?.disconnect()
    containerResizeObserverRef.current = null

    if (node) {
      containerResizeObserverRef.current = new ResizeObserver(scheduleMeasure)
      containerResizeObserverRef.current.observe(node)

      scheduleMeasure()
    }
  }

  useLayoutEffect(() => {
    actionsRef.current = actions

    actionWidthsByKeyRef.current =
      getRequestProfileHeaderFooterCachedActionWidths({
        actions,
        actionWidthsByKey: actionWidthsByKeyRef.current,
      })
    actionButtonRefs.current = {}

    scheduleMeasure()
  }, [actions, scheduleMeasure])

  useLayoutEffect(() => {
    return () => {
      cancelMeasure()
    }
  }, [cancelMeasure])

  function getActionButtonRef(actionKey) {
    return node => {
      if (node) {
        actionButtonRefs.current[actionKey] = node
        actionWidthsByKeyRef.current[actionKey] = node.offsetWidth
        scheduleMeasure()
      } else {
        delete actionButtonRefs.current[actionKey]
      }
    }
  }

  const hiddenActions = actions.slice(visibleActionsCount)

  const visibleActions = actions.slice(0, visibleActionsCount)

  return {
    addNewTextMeasureRef,
    addNewTextRef,
    getActionButtonRef,
    handleContainerRef,
    hiddenActions,
    moreButtonMeasureRef,
    visibleActions,
  }
}
